import * as React from "react";
import {withAppStores} from "../../../AppStores";
import type {IViewColumn, IViewSort} from "../../../data/models/ViewUtils";
import {SortDirection} from "../../../data/models/ViewUtils";
import {SVGIcon} from "../button/SVGIcon";
import {ReactUtils} from "../../utils/ReactUtils";
import type {IFieldPointer} from "../../../data/models/field/Field";
import {PointerDetectorReact} from "../../interaction/PointerDetectorReact";
import type {Pointer} from "../../../utils/interaction/Pointer";
import {XyiconFeature} from "../../../generated/api/base";
import type {AppState} from "../../../data/state/AppState";
import {Functions} from "../../../utils/function/Functions";
import {NameOfIconField} from "../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {ManageColumnCallback} from "./TableHeaderDropDown";
import {TableHeaderDropDown} from "./TableHeaderDropDown";
import type {ColumnDropIndicatorSide} from "./TableUtils";
import {minHeaderWidth, minIconHeaderWidth, tableColumnResizeCursorClassName} from "./TableConstants";
import {getSortCicleNextElement} from "./TableUtils";

interface ITableHeaderProps<T> {
	readonly header: Partial<IViewColumn>;
	readonly index: number;
	readonly sort?: IViewSort;
	readonly sortIndex: number;
	readonly multipleSorts: boolean;
	readonly dragSource: boolean;
	readonly dragTarget: boolean;
	readonly dragTargetSide: ColumnDropIndicatorSide | null;
	readonly onDragStart: (index: number, event: React.DragEvent) => void;
	readonly onDragEnd: (index: number, event: React.DragEvent) => void;
	readonly onDragEnter: (index: number, event: React.DragEvent) => void;
	readonly onDragOver: (index: number, event: React.DragEvent) => void;
	readonly onDragLeave: (index: number, event: React.DragEvent) => void;
	readonly onColResizeStart: (index: number, pointer: Pointer) => void;
	readonly onColResizeMove: (index: number, pointer: Pointer, header: Partial<IViewColumn>) => void;
	readonly onColResizeEnd?: (index: number, pointer: Pointer, header: Partial<IViewColumn>) => void;
	readonly onToggleSort?: (column: IFieldPointer) => void;
	readonly onAutoResizeColumn: (index: number, header: Partial<IViewColumn>) => void;
	readonly onManageColumns: ManageColumnCallback;
	readonly onSortAsc?: (id: IFieldPointer) => void;
	readonly onSortDesc?: (id: IFieldPointer) => void;
	readonly onClearSort?: (id: IFieldPointer) => void;
	readonly allowHide: boolean;
	readonly feature: XyiconFeature;
	readonly appState?: AppState;
}

interface ITableHeaderState<T> {
	hover: boolean;
	open: boolean;
	sortOrder: SortDirection;
	isColBeingResized: boolean;
}

@withAppStores("appState")
export class TableHeader<T> extends React.Component<ITableHeaderProps<T>, ITableHeaderState<T>> {
	private readonly _resizerRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
	private _isColumnResizeCursorActive = false;
	private _isColumnResizeActive = false;

	constructor(props: ITableHeaderProps<T>) {
		super(props);
		this.state = {
			hover: false,
			open: false,
			sortOrder: this.props.sort?.direction,
			isColBeingResized: false,
		};
	}

	public static getDerivedStateFromProps(props: ITableHeaderProps<any>, state: ITableHeaderState<any>) {
		if (!state.sortOrder && props.sort?.direction) {
			return {sortOrder: props.sort?.direction};
		}
		return null;
	}

	private onDragStart = (event: React.DragEvent) => {
		if (this._isColumnResizeActive || this.isResizeHandleEvent(event)) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		this.props.onDragStart(this.props.index, event);
	};

	private onDragEnd = (event: React.DragEvent) => {
		this.props.onDragEnd(this.props.index, event);
	};

	private onDragEnter = (event: React.DragEvent) => {
		this.props.onDragEnter(this.props.index, event);
	};

	private onDragOver = (event: React.DragEvent) => {
		this.props.onDragOver(this.props.index, event);
	};

	private onDragLeave = (event: React.DragEvent) => {
		this.props.onDragLeave(this.props.index, event);
	};

	private onColResizeStart = (pointer: Pointer) => {
		this._isColumnResizeActive = true;
		this.setColumnResizeCursor();
		this.setState({
			isColBeingResized: true,
		});
		this.props.onColResizeStart(this.props.index, pointer);
	};

	private onColResizeMove = (pointer: Pointer) => {
		this.props.onColResizeMove(this.props.index, pointer, this.props.header);
	};

	private onColResizeEnd = (pointer: Pointer) => {
		this._isColumnResizeActive = false;
		this.clearColumnResizeCursor();
		this.setState({
			isColBeingResized: false,
		});
		this.props.onColResizeEnd?.(this.props.index, pointer, this.props.header);
	};

	private setColumnResizeCursor() {
		document.body.classList.add(tableColumnResizeCursorClassName);
		this._isColumnResizeCursorActive = true;
	}

	private clearColumnResizeCursor() {
		if (this._isColumnResizeCursorActive) {
			document.body.classList.remove(tableColumnResizeCursorClassName);
			this._isColumnResizeCursorActive = false;
		}
	}

	private onResizerMouseDown = (event: React.MouseEvent) => {
		if (event.button !== 0) {
			return;
		}

		this._isColumnResizeActive = true;
		event.preventDefault();
		event.stopPropagation();
	};

	private isResizeHandleEvent(event: React.SyntheticEvent) {
		const target = event.target;

		return target instanceof Element && target.closest(".resizer") !== null;
	}

	private onHeaderClick = (event: React.MouseEvent) => {
		if (!(event.target as HTMLElement).classList.contains("resizer")) {
			this.props.onToggleSort(this.props.header.field);

			const newSortOrder = getSortCicleNextElement(this.state.sortOrder);

			this.setState({sortOrder: newSortOrder});
		}
	};

	private onHeaderDoubleClick = () => {
		this.props.onAutoResizeColumn(this.props.index, this.props.header);
	};

	private onMouseOver = () => {
		this.setState({hover: true});
	};

	private onMouseOut = () => {
		this.setState({hover: false});
	};

	private onOpenChange = (openValue: boolean, hoverValue?: boolean) => {
		this.setState({open: openValue});
		if (hoverValue !== undefined) {
			this.setState({hover: hoverValue});
		}
	};

	private onManageColumns = () => {
		this.props.onManageColumns({
			type: "manage",
			index: this.props.index,
		});
	};

	private onHideColumn = () => {
		this.props.onManageColumns({
			type: "hide",
			index: this.props.index,
		});
	};

	private onSortAsc = () => {
		const {appState, header, feature, onSortAsc} = this.props;

		onSortAsc?.(header.field);
		const realFeature = this.props.appState.app.spaceViewRenderer.isMounted ? XyiconFeature.SpaceEditor : feature;
		const view = appState.actions.getSelectedView(realFeature);

		view?.addSort({column: header.field, direction: SortDirection.ASC});

		this.setState({sortOrder: SortDirection.ASC});
	};

	private onSortDesc = () => {
		const {appState, header, feature, onSortDesc} = this.props;

		onSortDesc?.(header.field);
		const realFeature = this.props.appState.app.spaceViewRenderer.isMounted ? XyiconFeature.SpaceEditor : feature;
		const view = appState.actions.getSelectedView(realFeature);

		view?.addSort({column: header.field, direction: SortDirection.DESC});

		this.setState({sortOrder: SortDirection.DESC});
	};

	private onClearSort = (e?: React.MouseEvent) => {
		e?.stopPropagation();

		const {appState, header, feature, onClearSort} = this.props;

		onClearSort?.(header.field);
		const realFeature = this.props.appState.app.spaceViewRenderer.isMounted ? XyiconFeature.SpaceEditor : feature;
		const view = appState.actions.getSelectedView(realFeature);

		view?.addSort({column: header.field, direction: null});

		this.setState({sortOrder: null});
	};

	public override componentDidUpdate() {
		if (this.props.sort?.column !== this.props.header.field && this.state.sortOrder) {
			this.setState({sortOrder: null});
		}
	}

	public override componentWillUnmount() {
		this.clearColumnResizeCursor();
	}

	public override render() {
		const {header, index, dragSource, dragTarget, dragTargetSide, allowHide, sortIndex, multipleSorts} = this.props;
		const {hover, open, sortOrder} = this.state;
		const iconField = header.field.includes("icon");

		return (
			<div
				className={ReactUtils.cls("th", {
					dragSource: dragSource,
					dragTarget: dragTarget,
					dragTargetLeft: dragTarget && dragTargetSide === "left",
					icon: header.field.includes("icon"),
				})}
				key={index}
				data-table-column-index={index}
				style={{
					width: iconField ? `${header.width || minIconHeaderWidth}px` : `${Math.max(header.width, minHeaderWidth) || minHeaderWidth}px`,
					minWidth: iconField ? `${header.width || minIconHeaderWidth}px` : `${Math.max(header.width, minHeaderWidth) || minHeaderWidth}px`,
				}}
				draggable={!this.state.isColBeingResized}
				onDragStart={this.onDragStart}
				onDragEnd={this.onDragEnd}
				onDragOver={this.onDragOver}
				onDragEnter={this.onDragEnter}
				onDragLeave={this.onDragLeave}
				onClick={this.onHeaderClick}
				onMouseEnter={this.onMouseOver}
				onMouseLeave={this.onMouseOut}
			>
				{iconField ? (
					<span className="label icon">{header.title || `Column ${index + 1}`}</span>
				) : (
					<span className="label">{header.title || `Column ${index + 1}`}</span>
				)}
				{sortOrder && this.props.sort?.column === header.field && !iconField && (
					<span className={ReactUtils.cls("sort sortIndicator", {large: multipleSorts})}>
						<SVGIcon icon={sortOrder === SortDirection.ASC ? "arrow-up" : "arrow-down"} />
						{multipleSorts && sortIndex + 1}
					</span>
				)}
				{sortOrder && hover && this.props.sort?.column === header.field && !iconField && (
					<div
						className="sort cancelSort"
						onClick={this.onClearSort}
					>
						<SVGIcon icon="cancel" />
					</div>
				)}
				{(hover || open) && (
					<TableHeaderDropDown
						open={open}
						onOpenChange={this.onOpenChange}
						dropdownOptions={[
							...(!iconField
								? [
										{
											label: "Sort Ascending",
											onSelect: this.onSortAsc,
											selected: sortOrder === SortDirection.ASC,
										},
										{
											label: "Sort Descending",
											onSelect: this.onSortDesc,
											selected: sortOrder === SortDirection.DESC,
										},
										{
											label: "Clear Sort",
											onSelect: this.onClearSort,
											disabled: !sortOrder,
										},
									]
								: []),
							...(this.props.onManageColumns
								? [
										{
											label: "Manage Columns",
											onSelect: this.onManageColumns,
										},
										...(allowHide
											? [
													{
														label: "Hide Column",
														onSelect: this.onHideColumn,
													},
												]
											: []),
									]
								: []),
						]}
					/>
				)}
				{header.title == NameOfIconField ? (
					<div className="resizer icon" />
				) : (
					<>
						<div
							className="resizer"
							onMouseDown={this.onResizerMouseDown}
							onDoubleClick={this.onHeaderDoubleClick}
							onClick={Functions.stopPropagation}
							ref={this._resizerRef}
						/>
						<PointerDetectorReact
							onDown={this.onColResizeStart}
							onMove={this.onColResizeMove}
							onUp={this.onColResizeEnd}
							refObject={this._resizerRef}
						/>
					</>
				)}
			</div>
		);
	}
}
